/**
 * A tenant's DISPLAYED plan name, and the billing rhythm and price under it.
 *
 * docs/plans/2026-08-07_PLATFORM_BILLING_DIRECTION.md §2.3, §2.10, §2.14.
 *
 * ── "plan" means two different things, and conflating them is a bug ──────
 *
 * 1. **The preset POINTER** — a real `billing_plans` row the tenant resolves to
 *    (`tenants.platform_plan_override`, or the ladder's floor). It is what the
 *    live write-freeze read consults and what seeding copied from.
 * 2. **The displayed NAME** — derived, and what this module produces.
 *
 * A tenant showing "Custom" **still points at a preset**. An earlier revision of
 * the plan doc conflated these in its own prose, which is why the distinction is
 * written down here rather than assumed.
 *
 * ── Custom is DERIVED from override records (0177) ───────────────────────
 *
 * A tenant is Custom iff it holds at least one AUTHORITATIVE override:
 *
 *     a `tenant_entitlement_overrides` row (storage / concurrency / free / extra)
 *   | an allowed-models override (`tenant_settings.allowed_models`)
 *   | a `tenant_feature_pricing` row
 *
 * `deriveCustomState` below is the one implementation. Note what it still does
 * NOT do: compare a tenant against its preset. D11's objection to a DIFF stands
 * — a diff ratcheted, because skipping one propagation left every skipped tenant
 * reading "Custom" forever, after which propagation deselected them by default
 * and the fleet drifted toward untouchable.
 *
 * But that objection was never an argument for a FLAG; it was an argument
 * against a COMPARISON. An override record is neither: it is a fact about what
 * an operator set, it cannot ratchet, and it cannot disagree with what the
 * tenant is actually inheriting — which a flag could, and did. The flag had to
 * stand for five independent axes at once, which is what made a fallback restore
 * arbitrate between clobbering a newer hand edit and preserving a marker for an
 * edit it had just reverted.
 *
 * `tenants.plan_customized_at` survives as display/audit metadata ("last
 * hand-edited at") and is NOT read here.
 *
 * **The mirror caveat, which callers MUST respect in their copy:** `'preset'`
 * means *"holds no overrides"*, **not** *"matches the preset"*. A preset can
 * move without a tenant being propagated, and that tenant still resolves to
 * `'preset'`. So a propagation prompt says "not hand-edited", never "still on
 * Default" — the second would be false whenever a preset had moved.
 *
 * **`plan_label` does not confer Custom.** Naming an arrangement is not the same
 * act as configuring one, and a label that conferred Custom would exclude a
 * fully-inheriting tenant from propagation over a piece of text.
 */

/** Which rule produced the displayed name. */
export type PlanLabelSource =
  /** Never hand-edited — shows the preset's name. NOT a claim that it matches. */
  | 'preset'
  /** Hand-edited, and the operator named it. */
  | 'custom-named'
  /** Hand-edited, unnamed — shows "Custom". */
  | 'custom'

/**
 * The authoritative override facts Custom is derived from.
 *
 * Three independent sources, deliberately NOT collapsed into one boolean by the
 * caller: the console has to say WHICH kind of customisation a tenant holds
 * ("ceilings" vs "model access" vs "pricing"), and a pre-collapsed flag is
 * exactly what made the old marker unable to answer that.
 */
export interface CustomStateInput {
  /** Count of `tenant_entitlement_overrides` rows (storage/concurrency/free/extra). */
  entitlementOverrideCount?: number
  /** `tenant_settings.allowed_models` is a non-empty list. `[]` ≡ null ≡ unrestricted. */
  hasAllowedModelsOverride?: boolean
  /** At least one `tenant_feature_pricing` row. */
  hasFeaturePricingOverride?: boolean
}

export interface CustomState {
  isCustom: boolean
  /** Which axes are overridden — for copy that must name them, never inferred. */
  reasons: Array<'ceilings' | 'models' | 'pricing'>
}

/**
 * THE derivation of Custom. Reads override records only — never
 * `plan_customized_at`, never `plan_label`, and never a diff against the preset.
 */
export function deriveCustomState(input: CustomStateInput): CustomState {
  const reasons: CustomState['reasons'] = []
  if ((input.entitlementOverrideCount ?? 0) > 0) reasons.push('ceilings')
  if (input.hasAllowedModelsOverride) reasons.push('models')
  if (input.hasFeaturePricingOverride) reasons.push('pricing')
  return { isCustom: reasons.length > 0, reasons }
}

/** Convenience predicate for call sites that need only the boolean. */
export function isTenantCustom(input: CustomStateInput): boolean {
  return deriveCustomState(input).isCustom
}

export interface PlanLabelInput extends CustomStateInput {
  /** `tenants.plan_label` — the operator's own name, if they gave one. */
  planLabel?: string | null
  /** The preset's display name. `null` when the pointer names a deleted row. */
  presetName?: string | null
}

export interface PlanLabel {
  /** What to render. */
  name: string
  source: PlanLabelSource
  isCustom: boolean
}

/** Shown when a tenant is Custom but unnamed. */
export const CUSTOM_PLAN_LABEL = 'Custom'
/** Shown when the preset pointer names a row that no longer exists. */
export const UNKNOWN_PRESET_LABEL = 'Unknown plan'

/**
 * THE one implementation of the naming. Four surfaces need it — the customers
 * list, the customer header, the propagation prompt and the fallback lever — and
 * CLAUDE.md records that every copy of a plan rule which has ever existed
 * disagreed with the others.
 *
 * Note what it does NOT do: compare anything. See the module header.
 */
export function tenantPlanLabel(input: PlanLabelInput): PlanLabel {
  // Derived from override RECORDS, not from `plan_customized_at`. A tenant
  // holding no overrides inherits, and therefore reads as its preset, whatever
  // any historical marker says about when it was last touched.
  const isCustom = isTenantCustom(input)
  const label = input.planLabel?.trim()

  // A label is only DISPLAYED while the tenant is Custom. It is deliberately not
  // cleared on convergence — that would destroy text a person typed, and they may
  // diverge again — so a tenant reverted to its preset must not keep rendering
  // "Acme Enterprise" as if the arrangement still stood.
  if (isCustom && label) return { name: label, source: 'custom-named', isCustom: true }
  if (isCustom) return { name: CUSTOM_PLAN_LABEL, source: 'custom', isCustom: true }

  // `presetName` is null when the pointer names a deleted plan. Fail SOFT with a
  // readable string rather than throwing: `planLayerCandidates` deliberately falls
  // through on a missing row, and a console that 500s because a plan was deleted
  // is worse than one that says so.
  return { name: input.presetName?.trim() || UNKNOWN_PRESET_LABEL, source: 'preset', isCustom: false }
}

// ─────────────────────────────────────────────────────────────────────────
// Billing cycle (D17/D18) — an INVOICING rhythm, never the allowance window
// ─────────────────────────────────────────────────────────────────────────

export type CycleUnit = 'day' | 'week' | 'month' | 'year'
export const CYCLE_UNITS: readonly CycleUnit[] = ['day', 'week', 'month', 'year'] as const

export interface BillingCycle {
  count: number
  unit: CycleUnit
}

/** What a preset falls back to, and what every pre-0164 row already means. */
export const DEFAULT_BILLING_CYCLE: BillingCycle = { count: 1, unit: 'month' }

export function isCycleUnit(v: unknown): v is CycleUnit {
  return typeof v === 'string' && (CYCLE_UNITS as readonly string[]).includes(v)
}

/**
 * The cycle in force: the tenant's override, else the preset's, else monthly.
 *
 * Both halves are all-or-nothing — a count without a unit is not a cycle, and the
 * DB CHECK enforces that pairing, so a half-set override falls through rather than
 * inventing a unit.
 */
export function resolveBillingCycle(
  tenant: { cycleCount?: number | null; cycleUnit?: string | null } | null | undefined,
  preset: { cycleCount?: number | null; cycleUnit?: string | null } | null | undefined,
): BillingCycle {
  for (const src of [tenant, preset]) {
    const count = src?.cycleCount
    const unit = src?.cycleUnit
    if (typeof count === 'number' && count > 0 && isCycleUnit(unit)) return { count, unit }
  }
  return DEFAULT_BILLING_CYCLE
}

/**
 * Advance a paid-through date by one cycle — what "Record payment" offers as the
 * default rather than making an operator type a date.
 *
 * Month arithmetic clamps rather than overflowing: one month from 31 January is
 * 28/29 February, not 3 March. `setUTCMonth` would roll over silently, which for
 * a paid-through date means a customer's term quietly gaining days.
 */
export function advanceByCycle(from: Date, cycle: BillingCycle): Date {
  const d = new Date(from.getTime())
  if (cycle.unit === 'day') { d.setUTCDate(d.getUTCDate() + cycle.count); return d }
  if (cycle.unit === 'week') { d.setUTCDate(d.getUTCDate() + cycle.count * 7); return d }
  const monthsToAdd = cycle.unit === 'year' ? cycle.count * 12 : cycle.count
  const day = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + monthsToAdd)
  const lastOfTarget = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(day, lastOfTarget))
  return d
}

/**
 * How many times a cycle occurs in a month — for the reprice warning ONLY.
 *
 * Approximate by construction (30.44 days/month), so it is fine for "≈ 4.3× per
 * month" in a confirmation dialog and must never touch a charged amount. The plan
 * is explicit that a cycle change never auto-converts a price: showing an operator
 * an approximate multiplier is help, silently writing one is inventing money.
 */
export function cyclesPerMonth(cycle: BillingCycle): number {
  const days = cycle.unit === 'day' ? cycle.count : cycle.unit === 'week' ? cycle.count * 7 : null
  if (days == null) return cycle.unit === 'year' ? 1 / (12 * cycle.count) : 1 / cycle.count
  return 30.436875 / days
}

// ─────────────────────────────────────────────────────────────────────────
// The amount due (D9/D13/D16)
// ─────────────────────────────────────────────────────────────────────────

/** Which rule produced the amount — rendered, so nobody infers which won. */
export type PriceSource = 'tenant-flat' | 'tenant-seat' | 'preset-seat'

export interface AmountDue {
  /** Minor units of `currency`. */
  amountMinor: number
  currency: string
  source: PriceSource
  /** True when a seat price is set but a flat amount overrides it. The UI must
   *  say so, or the operator edits a control that does nothing. */
  seatPriceInert: boolean
  cycle: BillingCycle
}

export interface AmountDueInput {
  /** LIVE member count — never `billing_subscriptions`' seat cache, which is 0
   *  or null for a manually-billed tenant and would render zero for everyone. */
  memberCount: number
  /**
   * Seats included free in the plan (the resolved tenant→plan value). Only members
   * BEYOND this are charged the seat price — the included seats are genuinely free.
   * Absent/0 = every member is charged (the old behaviour). Never applies to a flat
   * amount, which is flat by definition.
   */
  includedSeats?: number | null
  tenant?: {
    flatAmountMinor?: number | null
    seatPriceMinor?: number | null
    priceCurrency?: string | null
    cycleCount?: number | null
    cycleUnit?: string | null
  } | null
  preset?: {
    seatPriceMinor?: number | null
    currency?: string | null
    cycleCount?: number | null
    cycleUnit?: string | null
  } | null
}

/**
 * The figure an operator invoices from, plus which rule produced it.
 *
 * Resolution: tenant flat → tenant seat × members → preset seat × billable,
 * where `billable = max(0, members − includedSeats)`.
 *
 * `includedSeats` is a PLAN attribute and applies ONLY to the plan's own
 * (preset-seat) pricing: the plan's included seats are free, and members beyond
 * them are charged the plan's seat price. It is NOT subtracted from a tenant's
 * BESPOKE contract — a negotiated flat or per-seat price is the whole deal and
 * charges what the operator set (a bespoke per-seat price × every member).
 * Otherwise a plan's free allotment would silently zero out an explicit contract.
 * (Phase 2 will let a tenant carry its OWN included seats for its own pricing.)
 *
 * Amounts are MINOR UNITS throughout and are never divided here. Formatting goes
 * through `formatCurrencyMinor`, because a hardcoded `/100` is a 100× error on JPY
 * (zero decimals) and a 10× one on BHD (three).
 */
export function resolveAmountDue(input: AmountDueInput): AmountDue {
  const t = input.tenant
  const p = input.preset
  const cycle = resolveBillingCycle(t, p)
  const members = Math.max(0, Math.trunc(input.memberCount))
  // Only seats BEYOND the plan's included allotment are charged at the PLAN's
  // seat price; included seats are free. Clamped so a bad count never bills
  // negative seats. Applies to the preset-seat branch only (see the docblock).
  const included = Math.max(0, Math.trunc(input.includedSeats ?? 0))
  const billable = Math.max(0, members - included)

  // `0` is a real price — a free bespoke arrangement — so presence is tested with
  // a null check, never truthiness. `flat ?? undefined` would treat 0 as unset.
  const hasFlat = t?.flatAmountMinor != null
  const hasSeat = t?.seatPriceMinor != null

  if (hasFlat) {
    return {
      amountMinor: t!.flatAmountMinor!,
      currency: t!.priceCurrency ?? p?.currency ?? '',
      source: 'tenant-flat',
      // The whole reason this field exists: a seat price behind a flat amount is
      // inert, and an operator who edits it must be told rather than left to
      // wonder why the total did not move.
      seatPriceInert: hasSeat,
      cycle,
    }
  }
  if (hasSeat) {
    return {
      // A bespoke contract seat price — charges every member. The plan's included
      // seats do NOT apply to a negotiated per-tenant price.
      amountMinor: t!.seatPriceMinor! * members,
      currency: t!.priceCurrency ?? p?.currency ?? '',
      source: 'tenant-seat',
      seatPriceInert: false,
      cycle,
    }
  }
  return {
    // The PLAN's seat price — only members beyond the included allotment.
    amountMinor: (p?.seatPriceMinor ?? 0) * billable,
    currency: p?.currency ?? '',
    source: 'preset-seat',
    seatPriceInert: false,
    cycle,
  }
}
